import { useEffect, useState } from 'react'
import { FilterProps, Renderer } from 'react-table'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cx from 'classnames'

import Button from 'common/components/button'
import CheckboxComponent from 'common/components/checkbox'
import { FilterHub } from 'common/icons'

type Props = {
  defaultFilters: string[]
}

function Filter({ defaultFilters }: Props) {
  return function SelectColumnFilter<D extends {}>({
    column: { filterValue, setFilter, preFilteredRows, id, filteredRows },
  }): Renderer<FilterProps<D>> {
    const [checkedItems, setCheckedItems] = useState<string[]>(
      filterValue || defaultFilters || []
    )
    const [isOpened, setIsOpened] = useState(false)

    const onClickCheckbox = (key) => {
      if (checkedItems.some((currentKey) => currentKey === key)) {
        setCheckedItems((checkedItems) =>
          checkedItems.filter((item) => item !== key)
        )
      } else {
        setCheckedItems((checkedItems) => [...checkedItems, key])
      }
    }
    const hasTableEnoughHeightToFitDropdown = filteredRows.length > 5

    useEffect(() => {
      setCheckedItems(defaultFilters)
      setFilter(defaultFilters)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultFilters])

    return (
      <DropdownMenu.Root open={isOpened} onOpenChange={setIsOpened}>
        <DropdownMenu.Trigger asChild>
          <Button
            className="h-auto updated-component"
            variant="icon"
            data-testid="filter-button"
            aria-label="Filter"
            prefixIcon={<FilterHub size={4} className="text-base-700" />}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            sideOffset={4}
            collisionPadding={8}
            className="z-40 px-4 pt-2 bg-gold-50 border border-gold-200 min-w-[200px] shadow-md"
          >
            {[
              {
                key: 'approved',
                onClick: onClickCheckbox,
                label: 'Approved',
              },
              { key: 'dropped', onClick: onClickCheckbox, label: 'Dropped' },
              { key: 'live', onClick: onClickCheckbox, label: 'Live' },
              { key: 'pending', onClick: onClickCheckbox, label: 'Pending' },
            ].map((option) => (
              <div
                key={option.key}
                className={cx(
                  'group flex items-center text-base-700 text-sm font-normal py-2 px-4 -mx-4',
                  {
                    'cursor-pointer hover:bg-gold-300': true,
                  }
                )}
                onClick={() => option.onClick(option.key)}
              >
                <CheckboxComponent
                  isChecked={checkedItems?.some((key) => key === option.key)}
                  onChange={() => option.onClick(option.key)}
                  className="mr-2 uppercase"
                  label={option.label}
                  labelColor="text-base-700"
                />
              </div>
            ))}
            <hr className="w-full my-1 border-gold-200" />
            <div className="flex justify-between py-1">
              <Button
                variant="link"
                onClick={() => {
                  setIsOpened(false)
                  setFilter(checkedItems)
                }}
              >
                OK
              </Button>
              <Button
                onClick={() => setCheckedItems(defaultFilters)}
                variant="link"
              >
                Reset
              </Button>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    )
  }
}

export default Filter
